import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { clsx } from 'clsx'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'

import { BaseConfirmationDialog, LoadingFeedback } from 'components'

import useIncidentManegement from 'hooks/useIncidentManegement'
import useIncidentHighRisk from 'hooks/useIncidentHighRisk'
import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'
import useStyles from './styles'
import schema from './schema'

const SpecificCriteria = ({ refresh }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pendingData, setPendingData] = useState(null)

  const {
    isShow = false,
    incident,
    wantsToAdvanceStep,
    resetAdvanceStep,
    isDpo,
    handleNext,
  } = useIncidentManegement()
  const { handleHighRiskNext } = useIncidentHighRisk()

  const { errors, control, getValues, triggerValidation, formState } = useForm({
    validationSchema: schema(),
  })

  const { dirtyFields } = formState

  const specificCriteria = incident?.highRisk?.specificCriteria
  const isCreate = !specificCriteria?.[0]?.answer
  const isConcluded = incident?.incidentManegementSteps?.find(
    (step) => step.step === 'high_risk',
  )?.concluded

  // Enviando apenas as respostas que foram editadas, para evitar enviar todas as respostas ao editar apenas uma
  const buildDirtyAnswers = (answers = []) => {
    const dirtyFieldNames = Array.from(dirtyFields || [])

    return answers.filter((_, index) =>
      dirtyFieldNames.some((fieldName) =>
        fieldName.startsWith(`answers[${index}].`),
      ),
    )
  }

  const handleConfirmDialog = () => {
    setOpen(false)
    onSubmit(pendingData)
  }

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      if (!isShow) {
        const answers = specificCriteria.map((item, index) => {
          const answerValue = data?.[`answers[${index}].answer`]
          const howValue = data?.[`answers[${index}].how`]
          const whyValue = data?.[`answers[${index}].why`]
          return {
            answer: answerValue,
            how: howValue,
            why: whyValue,
            incidentHighRiskQuestionId: item.id,
          }
        })
        const dirtyAnswers = buildDirtyAnswers(answers)

        if (isCreate) {
          await service.dponet.incidents.createHighRiskSpecificCriteria({
            incidentId: incident?.id,
            highRiskAnswers: { answers },
          })
        } else if (dirtyAnswers.length) {
          await service.dponet.incidents.updateHighRiskSpecificCriteria({
            incidentId: incident?.id,
            highRiskAnswers: { answers: dirtyAnswers },
          })
        }
      }

      if (!isDpo && !isConcluded) {
        await service.dponet.incidents.concludeStep({
          incidentId: incident.id,
          step: 'high_risk',
        })
      }

      refresh()
      resetAdvanceStep()
      !isDpo ? handleNext() : handleHighRiskNext()
    } catch (error) {
      snackbar.open({
        message: 'Ocorreu um erro ao enviar as respostas',
        variant: 'error',
      })
    } finally {
      setLoading(false)
      resetAdvanceStep()
    }
  }

  useEffect(() => {
    const handleValidateAdvanceStep = async () => {
      if (wantsToAdvanceStep) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        const isValid = await triggerValidation()

        if (isValid) {
          const data = getValues()
          const incidentType = incident?.incidentType
          const needsDpoDialog =
            incidentType !== constants.documents.TEST_INCIDENT_VALUE &&
            !incident?.dpoNotified

          if (needsDpoDialog) {
            setPendingData(data)
            setOpen(true)
            resetAdvanceStep()
            return
          }

          onSubmit(data)
        }
      }
      resetAdvanceStep()
    }

    handleValidateAdvanceStep()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <>
      <LoadingFeedback open={loading} />
      <form onSubmit={(e) => e.preventDefault()}>
        {specificCriteria?.map((specificCriterion, index) => (
          <Box key={specificCriterion?.id}>
            <Box mx={3} my={2} id={specificCriterion?.id}>
              <Grid container key={index}>
                <Grid item md={10} sm={12}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h5">
                        {specificCriterion?.content}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl error={!!errors?.answers?.[index]?.answer}>
                        <Controller
                          control={control}
                          name={`answers[${index}].answer`}
                          error={!!errors?.answers?.[index]?.answer}
                          helperText={errors?.answers?.[index]?.answer?.message}
                          defaultValue={specificCriterion?.answer?.answer}
                          as={
                            <RadioGroup row>
                              <Box>
                                <FormControlLabel
                                  value="Sim"
                                  control={<Radio color="primary" />}
                                  label="Sim"
                                  disabled={isShow}
                                  className={clsx({
                                    [classes.radioError]:
                                      !!errors?.answers?.[index]?.answer,
                                  })}
                                />
                                <FormControlLabel
                                  value="Não"
                                  control={<Radio color="primary" />}
                                  label="Não"
                                  disabled={isShow}
                                  className={clsx({
                                    [classes.radioError]:
                                      !!errors?.answers?.[index]?.answer,
                                  })}
                                />
                              </Box>
                            </RadioGroup>
                          }
                          onChange={([e]) => e.target.value}
                        />
                      </FormControl>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Controller
                            label="Como?"
                            control={control}
                            name={`answers[${index}].how`}
                            defaultValue={specificCriterion?.answer?.how}
                            error={!!errors?.answers?.[index]?.how}
                            helperText={errors?.answers?.[index]?.how?.message}
                            as={
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={5}
                                placeholder="Como?"
                                disabled={isShow}
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Controller
                            label="Por que?"
                            control={control}
                            name={`answers[${index}].why`}
                            defaultValue={specificCriterion?.answer?.why}
                            error={!!errors?.answers?.[index]?.why}
                            helperText={errors?.answers?.[index]?.why?.message}
                            as={
                              <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                maxRows={5}
                                placeholder="Por Que?"
                                disabled={isShow}
                              />
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        ))}
      </form>
      <BaseConfirmationDialog
        open={open}
        setOpen={setOpen}
        title="O Encarregado de Proteção de Dados(DPO) será notificado!"
        text="Comunicaremos o Encarregado de Proteção de Dados(DPO) do registro deste novo incidente. Você será notificado quando o parecer for concluído."
        actionButtonText="Continuar"
        variant="blueConfirmation"
        dialogSize="sm"
        actionButton={handleConfirmDialog}
      />
    </>
  )
}

SpecificCriteria.propTypes = {
  refresh: PropTypes.func,
}

export default SpecificCriteria
